<template lang="pug">
.cards.flex.flex-wrap.items-stretch.w-full(
  :class="[`cards--${screen.cards.length}`, screen.cols === 'content' && !screen.center ? 'justify-start' : 'justify-center']"
  :data-completables="completableIds"
)
  //- @mouseup.middle for middle-clicking a link
  uc-animate.relative(
    :aria-label="cardLabel(card)"
    :animate="card.animate || animate"
    :class="[screen.arrows ? `arrows-card arrows-card--${i + 1}` : '', `card card--cols-${card.cols || screen.cols || 6}`, !isEditModeActive && 'group', card.opens && 'rounded focus-visible_ring-8']"
    :data-completed="card.opens ? !!suspend.completed[card.opens] : null"
    :id="card.id"
    :is="component(card)"
    @click="() => !isEditModeActive && maybeOpen(card)"
    @mouseup.middle="() => !isEditModeActive && maybeOpen(card)"
    v-bind="linkAttrs(card)"
    v-for="({ heading, subheading, title, ...card }, i) in screen.cards"
  )
    transition-pop
      .card-corner-check.absolute.top-0.right-0.h-8.w-8.rounded-full.bg-primary-500.text-white.shadow-sm.flex.items-center.justify-center.z-10(
        class="translate-y-[-50%] translate-x-[50%]"
        v-if="card.opens && suspend.completed[card.opens]"
      )
        uc-icon(icon="theme:check-bold")

    div.absolute.bottom-1.right-1.z-50.rounded-lg.px-2.py-1.bg-indigo-700.hover_bg-indigo-500.border-white.text-white.cursor-pointer.flex.items-center.gap-1(
      role="button"
      tabindex="0"
      @click.stop="openLightbox(card.opens)"
      @keydown.enter.stop="openLightbox(card.opens)"
      v-if="isEditModeActive && isLightbox(card)"
    )
      uc-icon.text-sm(icon="mdi:open-in-new")
      uc-text.text-sm(text="Open")

    .transition.card-surface(
      :class="[{ 'cursor-pointer hover_brightness-[1.1] hover_opacity-90': card.opens }, mergeStyles(`bg-paper h-full surface p-6 corners focus-visible_ring-4 bg-cover`, screen.styles?.card, card.style, screen.banner || card.banner ? 'p-0' : '')]"
      :style="[{ '--arrow-card-color': `var(--color-secondary-${200 + i * 100})` }, card.bg ? { backgroundImage: setBgImgURLs(card.bg) } : {}]"
    )
      .arrows-card-triangle(v-if="screen.arrows")
      template(v-if="screen.banner || card.banner")
        .card--banner
          div(
            :class="[{'min-h-10': !(heading || subheading || title)}, twMerge('p-6 bg-primary-500', card.center ? 'text-center' : '', screen.styles?.banner, card.styles?.banner)]"
          )
            uc-editable(
              :class="twMerge('text-white inline-block', subheading ? 'subheading' : 'heading', card.styles?.title, card.styles?.heading, card.styles?.subheading)"
              :editPath="`${editPath}.cards.${i}.${heading ? 'heading' : subheading ? 'subheading' : title ? 'title' : null}`"
              :k="heading ? `${card.id}.heading` : subheading ? `${card.id}.subheading` : `${card.id}.title`"
              :text="heading || subheading || title"
              :type="card.h ? card.h : subheading ? 'h4' : 'h3'"
              v-if="heading || subheading || title"
            )
          .p-6
            uc-content(:edit-path="`${editPath}.cards.${i}`" :screen="card")
      template(v-else="")
        uc-content(
          :edit-path="`${editPath}.cards.${i}`"
          :screen="{ heading, subheading, title, ...card }"
        )
</template>

<script setup lang="ts">
import { twMerge } from 'tailwind-merge';
import {
  computed,
  inject,
  nextTick,
  onMounted,
  ref,
  toRef,
  watch,
  withDefaults,
  type Ref
} from 'vue';
import mergeStyles from '../lib/mergeStyles';
import openLightbox from '../lib/openLightbox';
import useSuspend from '../lib/useSuspend';
import { useCompletables } from '../stores/useLockables';
import useTheme from '../stores/useTheme';
import { computeConditionIds } from '../lib/evaluateCondition';
import { setCompleted, set } from '../lib/SCORM';
import normalizeMarkdown from '../lib/normalizeMarkdown';
import isQuizPage from '../lib/isQuizPage';
import bugAlert from '../lib/bugAlert';
import expandUploadPath from '../lib/expandUploadPath';
import cardsSchema from '../lib/screen-schemas/screen-cards.schema';
import useScreenAudio from '../lib/useScreenAudio';
import { clearQueue, play, stop } from '../lib/audio';

const setBgImgURLs = (path: string) => {
  const paths = path.trim().split(' ').map(expandUploadPath);
  return paths.length === 1
    ? `url(${paths[0]})`
    : paths.map(p => `url(${p})`).join(', ');
};

type Card = UcScreen & { opens?: string };

const props = withDefaults(
  defineProps<{
    editPath: string;
    screen: UcScreen & {
      cards: Card[];
      arrows: boolean;
    };
    /**
     * When true, a nested `uc-screen` (e.g. HSG intro/result or `templates[]`) owns
     * section intro audio — do not react to `sectionActive` here.
     */
    nested?: boolean;
  }>(),
  { nested: false }
);

const suspend = useSuspend();
const theme = useTheme();
const urlRegex = /^https?:\/\//;
const isEditModeActive = inject('isEditModeActive') as Ref<boolean>;
const pageId = inject('pageId') as object;
const sectionActive = inject<Ref<boolean>>('sectionActive', ref(false));
const { resolveSlot } = useScreenAudio(toRef(props, 'screen'), cardsSchema);

/** Invalidate in-flight JiT resolve when the section deactivates mid-synthesis. */
let introPlayGeneration = 0;

const playIntroIfStillActive = async () => {
  if (props.nested) return;
  if (!sectionActive.value || !props.screen.id) return;

  const gen = ++introPlayGeneration;
  clearQueue();
  stop();

  const url = await resolveSlot('intro');
  if (gen !== introPlayGeneration || !sectionActive.value || !url) return;

  await play(url, { owner: props.screen.id });
};

watch(sectionActive, active => {
  if (!active) {
    introPlayGeneration++;
    return;
  }
  void playIntroIfStillActive();
});

const animate = computed(
  () => props.screen['animate-card'] || theme.value['animate-card']
);

type OpenableCard = Card & { opens: string };

const isLink = (card: Card): card is OpenableCard =>
  Boolean(card.opens && urlRegex.test(card.opens));

const isLightbox = (card: Card): card is OpenableCard =>
  Boolean(card.opens && !isLink(card));

const completableIds = computed(() => {
  if ('choose' in props.screen && props.screen.choose === 0)
    return props.screen.id;
  return [
    ...props.screen.cards.filter(isLightbox).map(c => c.opens),
    ...computeConditionIds(props.screen.complete)
  ];
});

useCompletables(completableIds);

const cardLabel = (card: Card) => {
  // no aria-label needed for divs
  if (component(card) === 'div') return;

  // search for the 1st meaningful piece of text to use as aria-label
  let normalizedAria = normalizeMarkdown(
    card.heading ||
      card.subheading ||
      card.title ||
      card['img-alt'] ||
      card['img-alt-top'] ||
      ''
  );
  if (!normalizedAria && card.opens && props.screen.templates) {
    const cardTemplate = props.screen.templates.find(t => t.id === card.opens);

    if (cardTemplate?.templates) {
      // just use the first template heading
      const subTemplate = cardTemplate.templates[0];
      normalizedAria = normalizeMarkdown(
        subTemplate?.heading ||
          subTemplate?.subheading ||
          subTemplate?.title ||
          ''
      );
      if (normalizedAria) {
        return normalizedAria;
      }
    }
    if (!normalizedAria)
      normalizedAria = normalizeMarkdown(
        cardTemplate?.heading ||
          cardTemplate?.subheading ||
          cardTemplate?.title ||
          cardTemplate?.['img-alt'] ||
          cardTemplate?.['img-alt-top'] ||
          ''
      );
  }

  return normalizedAria;
};

const component = (card: Card) =>
  isLink(card) ? 'a' : card.opens ? 'button' : 'div';

const maybeOpen = (card: Card) => {
  if (isLightbox(card)) openLightbox(card.opens);
  if (isLink(card) && card.id) setCompleted(card.id);
};

const linkAttrs = (card: Card) =>
  isLink(card)
    ? {
        href: card.opens,
        rel: 'noopener noreferrer',
        target: '_blank'
      }
    : {};

onMounted(async () => {
  await nextTick();
  if (sectionActive.value) void playIntroIfStillActive();

  if (
    'choose' in props.screen &&
    props.screen.choose === 0 &&
    completableIds.value === props.screen.id
  ) {
    setCompleted(props.screen.id);
  }
});

const app = document.querySelector('#app');
app?.addEventListener('quizReset', async e => {
  if (await isQuizPage(e, pageId)) {
    completableIds?.value?.forEach((id, _i) => {
      set(`completed.${id}`, false);
    });
  }
});
</script>

<style>
.arrows-card {
  position: relative;
  @apply drop-shadow-md;
}

:is(.arrows-card .card-surface) {
  border-width: 2px;
  border-style: solid;
  border-left-width: 10px;

  /* arrow color is set with a style attr on each .card-surface, which is where the secondary color from the YAML is also set */
  border-color: rgb(var(--arrow-card-color));
}

html[dir='rtl'] :is(.arrows-card .card-surface) {
  border-left-width: 0px;
  border-right-width: 10px;
}

:is(.arrows-card .arrows-card-triangle) {
  content: '';
  position: absolute;
  right: -31px;
  top: 50%;
  transform: translateY(-50%);
  border-top: 32px solid transparent;
  border-bottom: 32px solid transparent;
  border-left: 32px solid transparent;
  border-left-color: rgb(var(--arrow-card-color));
}

html[dir='rtl'] :is(.arrows-card .arrows-card-triangle) {
  right: 100%;
  border-left: 0px solid transparent;
  border-right: 32px solid transparent;
  border-right-color: rgb(var(--arrow-card-color));
}

.arrows-card:last-child .arrows-card-triangle {
  display: none;
}
</style>
